/**
 * Timezone-correct helpers for a single agency timezone.
 *
 * GTFS times are seconds since noon-minus-12h on the service date and legitimately exceed
 * 24h, so every conversion has to go through the agency's local calendar rather than UTC.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
export const DAY_SECONDS = 86_400;

export interface ZonedParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
	/** 0 = Sunday, matching GTFS calendar.txt column order after Monday..Sunday remap. */
	weekday: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
	let cached = formatterCache.get(timeZone);
	if (!cached) {
		cached = new Intl.DateTimeFormat("en-US", {
			timeZone,
			hourCycle: "h23",
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			weekday: "short",
		});
		formatterCache.set(timeZone, cached);
	}
	return cached;
}

const WEEKDAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function zonedParts(epochMs: number, timeZone: string): ZonedParts {
	const parts = formatter(timeZone).formatToParts(new Date(epochMs));
	const lookup: Record<string, string> = {};
	for (const part of parts) {
		if (part.type !== "literal") {
			lookup[part.type] = part.value;
		}
	}
	return {
		year: Number(lookup.year),
		month: Number(lookup.month),
		day: Number(lookup.day),
		hour: Number(lookup.hour),
		minute: Number(lookup.minute),
		second: Number(lookup.second),
		weekday: WEEKDAY_INDEX[lookup.weekday] ?? 0,
	};
}

function offsetMs(epochMs: number, timeZone: string): number {
	const parts = zonedParts(epochMs, timeZone);
	const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
	return asUtc - Math.floor(epochMs / 1000) * 1000;
}

/**
 * Convert a local wall-clock time to epoch ms. Two passes settle DST boundaries: the first
 * guess uses the offset at the naive instant, the second uses the offset at the corrected one.
 */
export function epochFromZoned(
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	second: number,
	timeZone: string,
): number {
	const naive = Date.UTC(year, month - 1, day, hour, minute, second);
	let guess = naive - offsetMs(naive, timeZone);
	guess = naive - offsetMs(guess, timeZone);
	return guess;
}

/** `YYYYMMDD`, the GTFS service-date form. */
export function serviceDateKey(epochMs: number, timeZone: string): string {
	const p = zonedParts(epochMs, timeZone);
	return `${p.year}${String(p.month).padStart(2, "0")}${String(p.day).padStart(2, "0")}`;
}

export function shiftServiceDateKey(key: string, days: number): string {
	const year = Number(key.slice(0, 4));
	const month = Number(key.slice(4, 6));
	const day = Number(key.slice(6, 8));
	const shifted = new Date(Date.UTC(year, month - 1, day + days));
	return `${shifted.getUTCFullYear()}${String(shifted.getUTCMonth() + 1).padStart(2, "0")}${String(shifted.getUTCDate()).padStart(2, "0")}`;
}

/** GTFS weekday index for a service date key: 0 = Monday .. 6 = Sunday. */
export function gtfsWeekdayIndex(key: string): number {
	const year = Number(key.slice(0, 4));
	const month = Number(key.slice(4, 6));
	const day = Number(key.slice(6, 8));
	const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
	return (jsDay + 6) % 7;
}

/**
 * Absolute instant for a GTFS stop time on a service date. `seconds` may exceed 86400,
 * which correctly rolls into the following calendar day.
 */
export function stopTimeToEpoch(serviceDate: string, seconds: number, timeZone: string): number {
	const year = Number(serviceDate.slice(0, 4));
	const month = Number(serviceDate.slice(4, 6));
	const day = Number(serviceDate.slice(6, 8));
	// GTFS anchors the day at noon-12h so that a DST shift moves the whole day, not the schedule.
	const noon = epochFromZoned(year, month, day, 12, 0, 0, timeZone);
	return noon - 12 * HOUR_MS + seconds * 1000;
}

/** Minutes since local midnight, for target windows that are expressed as clock times. */
export function minutesSinceMidnight(epochMs: number, timeZone: string): number {
	const p = zonedParts(epochMs, timeZone);
	return p.hour * 60 + p.minute + p.second / 60;
}

/** Epoch ms for `minutes` past local midnight on the same local day as `referenceMs`. */
export function epochAtMinutes(referenceMs: number, minutes: number, timeZone: string): number {
	const p = zonedParts(referenceMs, timeZone);
	const midnight = epochFromZoned(p.year, p.month, p.day, 0, 0, 0, timeZone);
	return midnight + Math.round(minutes * MINUTE_MS);
}

export function formatClock(epochMs: number, timeZone: string): string {
	const p = zonedParts(epochMs, timeZone);
	const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
	return `${hour12}:${String(p.minute).padStart(2, "0")}`;
}

/** Lower case, matching the clock the app itself draws: "3:41 pm", never "3:41 PM". */
export function formatClockWithMeridiem(epochMs: number, timeZone: string): string {
	const p = zonedParts(epochMs, timeZone);
	return `${formatClock(epochMs, timeZone)} ${p.hour < 12 ? "am" : "pm"}`;
}

/** Great-circle distance in metres. Used only for profile proximity, so accuracy is ample. */
export function distanceMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
	const earthRadius = 6_371_000;
	const toRad = Math.PI / 180;
	const dLat = (b.lat - a.lat) * toRad;
	const dLon = (b.lon - a.lon) * toRad;
	const lat1 = a.lat * toRad;
	const lat2 = b.lat * toRad;
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
	return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Shortest distance in metres from a point to a polyline.
 *
 * Projects to a local flat plane centred on the point first. Over a single metro area the
 * error is negligible, and it turns the spherical problem into ordinary point-to-segment
 * geometry.
 */
export function distanceToPathMeters(point: { lat: number; lon: number }, path: Array<{ lat: number; lon: number }>): number {
	if (path.length === 0) {
		return Number.POSITIVE_INFINITY;
	}
	if (path.length === 1) {
		return distanceMeters(point, path[0]);
	}

	const metresPerDegreeLat = 111_320;
	const metresPerDegreeLon = metresPerDegreeLat * Math.cos((point.lat * Math.PI) / 180);
	const project = (position: { lat: number; lon: number }) => ({
		x: (position.lon - point.lon) * metresPerDegreeLon,
		y: (position.lat - point.lat) * metresPerDegreeLat,
	});

	let shortest = Number.POSITIVE_INFINITY;
	for (let i = 0; i < path.length - 1; i += 1) {
		const a = project(path[i]);
		const b = project(path[i + 1]);
		const dx = b.x - a.x;
		const dy = b.y - a.y;
		const lengthSquared = dx * dx + dy * dy;
		const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, -(a.x * dx + a.y * dy) / lengthSquared));
		const closestX = a.x + t * dx;
		const closestY = a.y + t * dy;
		shortest = Math.min(shortest, Math.hypot(closestX, closestY));
	}
	return shortest;
}
